import React, { useState, useEffect } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config/apiconfig";


const API_URL = `${API_BASE_URL}/api`;

const SalaryService = {
  getCategories: async (companyId, token, userType) => {
    try {
      const response = await axios.get(`${API_URL}/salary-categories`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching salary categories:", error);
      throw error;
    }
  },
  addCategory: async (category, companyId, token, userType) => {
    try {
      const response = await axios.post(`${API_URL}/salary-categories`, category, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error adding salary category:", error);
      throw error;
    }
  },
  deleteCategory: async (id, companyId, token, userType) => {
    try {
      await axios.delete(`${API_URL}/salary-categories/${id}`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
    } catch (error) {
      console.error("Error deleting salary category:", error);
      throw error;
    }
  },
  getPayrolls: async (companyId, token, userType) => {
    try {
      const response = await axios.get(`${API_URL}/payrolls`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching payrolls:", error);
      throw error;
    }
  },
  calculateSalary: async (empNo, categoryId, companyId, token, userType) => {
    try {
      const response = await axios.post(`${API_URL}/calculate-salary`, {
        emp_no: empNo,
        category_id: categoryId
      }, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error calculating salary:", error);
      throw error;
    }
  },
  addPayroll: async (payroll, companyId, token, userType) => {
    try {
      const response = await axios.post(`${API_URL}/payrolls`, payroll, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error adding payroll:", error);
      throw error;
    }
  },
  deletePayroll: async (id, companyId, token, userType) => {
    try {
      await axios.delete(`${API_URL}/payrolls/${id}`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
    } catch (error) {
      console.error("Error deleting payroll:", error);
      throw error;
    }
  },
  getEmployees: async (companyId, token, userType) => {
    try {
      const response = await axios.get(`${API_URL}/employees`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching employees:", error);
      throw error;
    }
  },
};

const CategoryForm = ({ onCategoryAdded, companyId, token, userType }) => {
  const [form, setForm] = useState({
    category_name: "",
    base_salary: "",
    percentage: "",
    deductions: "",
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await SalaryService.addCategory(form, companyId, token, userType);
      setForm({ category_name: "", base_salary: "", percentage: "", deductions: "" });
      setSuccess("Salary category added successfully!");
      setError(null);
      onCategoryAdded();
    } catch (error) {
      setError(error.response?.data?.error || "Failed to add salary category");
    }
  };

  return (
    <div className="w-full lg:w-1/2 bg-white p-6 rounded-lg shadow-lg">
      <h3 className="text-xl font-bold mb-4">Add Salary Category</h3>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg mb-4">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-600 p-4 rounded-lg mb-4">
          {success}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <input
          name="category_name"
          placeholder="Category Name"
          value={form.category_name}
          onChange={handleChange}
          required
          className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          name="base_salary"
          placeholder="Base Salary"
          type="number"
          step="0.01"
          value={form.base_salary}
          onChange={handleChange}
          required
          className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          name="percentage"
          placeholder="Percentage (e.g., 0.2)"
          type="number"
          step="0.01"
          value={form.percentage}
          onChange={handleChange}
          required
          className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          name="deductions"
          placeholder="Deductions"
          type="number"
          step="0.01"
          value={form.deductions}
          onChange={handleChange}
          required
          className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button className="w-full p-3 bg-green-500 text-white rounded-lg hover:bg-green-600">
          Add Category
        </button>
      </form>
    </div>
  );
};

const CategoriesList = ({ categories, onDeleteCategory, companyId, token, userType }) => (
  <div className="w-full lg:w-1/2 bg-white p-6 rounded-lg shadow-lg">
    <h3 className="text-xl font-bold mb-4">Salary Categories</h3>
    <div className="overflow-x-auto">
      <table className="w-full min-w-max whitespace-nowrap border-collapse bg-white rounded-lg shadow-md overflow-hidden">
        <thead>
          <tr className="bg-blue-600 text-white">
            <th className="p-3 text-left font-semibold">Category Name</th>
            <th className="p-3 text-left font-semibold">Formula</th>
            <th className="p-3 text-left font-semibold">Action</th>
          </tr>
        </thead>
        <tbody>
          {categories.map((cat) => (
            <tr key={cat.id} className="odd:bg-gray-50 even:bg-gray-100 hover:bg-gray-200">
              <td className="p-3 border-b border-gray-200">{cat.category_name}</td>
              <td className="p-3 border-b border-gray-200">{cat.formula}</td>
              <td className="p-3 border-b border-gray-200">
                <button
                  onClick={() => onDeleteCategory(cat.id, companyId, token, userType)}
                  className="text-red-600 hover:text-red-800"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const PayrollForm = ({ categories, onPayrollAdded, companyId, token, userType }) => {
  const [employees, setEmployees] = useState([]);
  const [payrollForm, setPayrollForm] = useState({
    emp_no: "",
    category_id: "",
    payroll_date: "",
  });
  const [calculatedSalary, setCalculatedSalary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const data = await SalaryService.getEmployees(companyId, token, userType);
        setEmployees(data);
      } catch (error) {
        console.error("Error fetching employees:", error);
        setError("Failed to fetch employees");
      }
    };
    fetchEmployees();
  }, [companyId, token, userType]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setPayrollForm({ ...payrollForm, [name]: value });
  };

  const handleCalculateSalary = async () => {
    if (!payrollForm.emp_no || !payrollForm.category_id) {
      setError("Please select both employee and salary category");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await SalaryService.calculateSalary(
        payrollForm.emp_no,
        payrollForm.category_id,
        companyId,
        token,
        userType
      );
      setCalculatedSalary(result.calculated_salary);
    } catch (error) {
      console.error("Error calculating salary:", error);
      setError(error.response?.data?.error || "Failed to calculate salary");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (calculatedSalary === null) {
      setError("Please calculate salary first");
      return;
    }

    try {
      await SalaryService.addPayroll({
        emp_no: payrollForm.emp_no,
        category_id: payrollForm.category_id,
        calculated_salary: calculatedSalary,
        payroll_date: payrollForm.payroll_date,
      }, companyId, token, userType);
      
      setPayrollForm({
        emp_no: "",
        category_id: "",
        payroll_date: "",
      });
      setCalculatedSalary(null);
      setSuccess("Payroll record added successfully!");
      setError(null);
      onPayrollAdded();
    } catch (error) {
      console.error("Error adding payroll:", error);
      setError(error.response?.data?.error || "Failed to add payroll record");
    }
  };

  return (
    <div className="w-full lg:w-1/2 bg-white p-6 rounded-lg shadow-lg">
      <h3 className="text-xl font-bold mb-4">Add Payroll Record</h3>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg mb-4">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-600 p-4 rounded-lg mb-4">
          {success}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <select
          name="emp_no"
          value={payrollForm.emp_no}
          onChange={handleFormChange}
          required
          className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select Employee</option>
          {employees.map((emp) => (
            <option key={emp.id} value={emp.empNo}>
              {emp.empNo} - {emp.name}
            </option>
          ))}
        </select>
        
        <select
          name="category_id"
          value={payrollForm.category_id}
          onChange={handleFormChange}
          required
          className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select Category</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.category_name}
            </option>
          ))}
        </select>
        
        <input
          name="payroll_date"
          type="date"
          value={payrollForm.payroll_date}
          onChange={handleFormChange}
          required
          className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        <button
          type="button"
          onClick={handleCalculateSalary}
          disabled={loading}
          className="w-full p-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 mb-4"
        >
          {loading ? "Calculating..." : "Calculate Salary"}
        </button>
        
        {calculatedSalary !== null && (
          <div className="text-center mb-4">
            <p className="text-lg font-bold">
              Calculated Salary: LKR {calculatedSalary.toFixed(2)}
            </p>
          </div>
        )}
        
        <button 
          type="submit"
          disabled={calculatedSalary === null}
          className="w-full p-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Add Payroll
        </button>
      </form>
    </div>
  );
};

const PayrollList = ({ payrolls, onDeletePayroll, companyId, token, userType }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().substring(0, 7));
  const [filteredPayrolls, setFilteredPayrolls] = useState([]);

  useEffect(() => {
    let filtered = payrolls;
    
    if (selectedMonth) {
      filtered = filtered.filter(payroll => 
        payroll.payroll_date && payroll.payroll_date.substring(0, 7) === selectedMonth
      );
    }
    
    // Filter by search query if provided
    if (searchQuery) {
      filtered = filtered.filter(payroll => 
        payroll.emp_no.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (payroll.employee_name && payroll.employee_name.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    setFilteredPayrolls(filtered);
  }, [payrolls, selectedMonth, searchQuery]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const downloadPayrollReport = (payroll) => {
    const doc = new jsPDF();
    
    doc.setFontSize(16);
    doc.text("PAYROLL REPORT", 105, 20, { align: "center" });
    
    doc.setFontSize(12);
    doc.text("---------------------------------------------------------------------------------", 20, 30);
    
    doc.text(`Employee Number: ${payroll.emp_no}`, 20, 40);
    doc.text(`Employee Name: ${payroll.employee_name || 'N/A'}`, 20, 50);
    doc.text(`Salary Category: ${payroll.category_name}`, 20, 60);
    doc.text(`Salary Amount: LKR ${payroll.calculated_salary}`, 20, 70);
    doc.text(`Payroll Date: ${formatDate(payroll.payroll_date)}`, 20, 80);
    
    doc.text("---------------------------------------------------------------------------------", 20, 90);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 20, 100);
    
    const fileName = `Payroll_${payroll.emp_no}_${formatDate(payroll.payroll_date).replace(/\//g, '-')}.pdf`;
    doc.save(fileName);
  };

  return (
    <div className="w-full lg:w-1/2 bg-white p-6 rounded-lg shadow-lg">
      <h3 className="text-xl font-bold mb-4">Payroll Records</h3>
      
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <div className="w-full md:w-1/2">
          <input
            type="text"
            placeholder="Search by Employee Number/Name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="w-full md:w-1/2">
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full min-w-max whitespace-nowrap border-collapse bg-white rounded-lg shadow-md overflow-hidden">
          <thead>
            <tr className="bg-blue-600 text-white">
              <th className="p-3 text-left font-semibold">Employee No</th>
              <th className="p-3 text-left font-semibold">Employee Name</th>
              <th className="p-3 text-left font-semibold">Category</th>
              <th className="p-3 text-left font-semibold">Calculated Salary</th>
              <th className="p-3 text-left font-semibold">Payroll Date</th>
              <th className="p-3 text-left font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredPayrolls.map((payroll) => (
              <tr key={payroll.id} className="odd:bg-gray-50 even:bg-gray-100 hover:bg-gray-200">
                <td className="p-3 border-b border-gray-200">{payroll.emp_no}</td>
                <td className="p-3 border-b border-gray-200">
                  {payroll.employee_name || "N/A"}
                </td>
                <td className="p-3 border-b border-gray-200">{payroll.category_name}</td>
                <td className="p-3 border-b border-gray-200">
                  {payroll.calculated_salary}
                </td>
                <td className="p-3 border-b border-gray-200">
                  {formatDate(payroll.payroll_date)}
                </td>
                <td className="p-3 border-b border-gray-200">
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => onDeletePayroll(payroll.id, companyId, token, userType)}
                      className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => downloadPayrollReport(payroll)}
                      className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                    >
                      Download Report
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SalaryPayrollManager = () => {
  const { user, userType, companyId, logout } = useAuth();
  const [categories, setCategories] = useState([]);
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCategories = async () => {
    if (!companyId) {
      logout();
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const data = await SalaryService.getCategories(companyId, token, userType);
      setCategories(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setError("Failed to fetch salary categories. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPayrolls = async () => {
    if (!companyId) {
      logout();
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const data = await SalaryService.getPayrolls(companyId, token, userType);
      setPayrolls(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching payrolls:", err);
      setError("Failed to fetch payroll records. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await SalaryService.deleteCategory(id, companyId, token, userType);
      setCategories((prevCategories) =>
        prevCategories.filter((category) => category.id !== id)
      );
      setError(null);
    } catch (err) {
      console.error("Error deleting category:", err);
      if (err.response && err.response.status === 400) {
        setError("This category cannot be deleted because it is used in payroll records.");
      } else {
        setError("Failed to delete category. Please try again later.");
      }
    }
  };

  const handleDeletePayroll = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await SalaryService.deletePayroll(id, companyId, token, userType);
      setPayrolls((prevPayrolls) =>
        prevPayrolls.filter((payroll) => payroll.id !== id)
      );
      setError(null);
    } catch (err) {
      console.error("Error deleting payroll:", err);
      setError("Failed to delete payroll record. Please try again later.");
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchPayrolls();
  }, [companyId]);

  return (
    <div className="w-full p-6 space-y-6 bg-gradient-to-r from-blue-900 to-blue-700 min-h-screen">
      <h1 className="text-4xl font-bold text-white mb-6 text-center">Salary & Payroll Management</h1>
      
      {companyId ? (
        <>
          {loading ? (
            <div className="text-center text-lg text-white">Loading...</div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg max-w-4xl mx-auto">
              {error}
            </div>
          ) : (
            <>
              <div className="flex flex-col lg:flex-row gap-6 mb-6">
                <CategoryForm 
                  onCategoryAdded={fetchCategories} 
                  companyId={companyId}
                  token={localStorage.getItem('token')}
                  userType={userType}
                />
                <CategoriesList 
                  categories={categories} 
                  onDeleteCategory={handleDeleteCategory}
                  companyId={companyId}
                  token={localStorage.getItem('token')}
                  userType={userType}
                />
              </div>
              
              <div className="flex flex-col lg:flex-row gap-6">
                <PayrollForm 
                  categories={categories} 
                  onPayrollAdded={fetchPayrolls}
                  companyId={companyId}
                  token={localStorage.getItem('token')}
                  userType={userType}
                />
                <PayrollList 
                  payrolls={payrolls} 
                  onDeletePayroll={handleDeletePayroll}
                  companyId={companyId}
                  token={localStorage.getItem('token')}
                  userType={userType}
                />
              </div>
            </>
          )}
        </>
      ) : (
        <div className="text-center text-lg text-white">
          Company information not available. Please login again.
        </div>
      )}
    </div>
  );
};

export default SalaryPayrollManager;